import { Outlet, Navigate } from 'react-router-dom'
import { Box, Chip, Stack, Typography } from '@mui/material'
import { AutoGraph, Radar, VerifiedUser } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/authStore'

export function AuthLayout() {
  const { t } = useTranslation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  if (isAuthenticated) return <Navigate to="/dashboard" replace />

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(420px, 1.08fr) minmax(480px, .92fr)' },
        alignItems: 'center',
        backgroundColor: 'background.default',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          minHeight: '100vh',
          p: { lg: 7, xl: 10 },
          color: '#fff',
          flexDirection: 'column',
          justifyContent: 'space-between',
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(145deg, #071A2D 0%, #0B3555 48%, #0876BA 100%)',
          '&::before': {
            content: '""', position: 'absolute', width: 520, height: 520, borderRadius: '50%',
            right: -180, top: -180, border: '1px solid rgba(255,255,255,.13)',
            boxShadow: '0 0 0 70px rgba(255,255,255,.035), 0 0 0 150px rgba(255,255,255,.018)',
          },
          '&::after': {
            content: '""', position: 'absolute', width: 360, height: 360, borderRadius: '50%',
            left: -190, bottom: -210, background: 'radial-gradient(circle, rgba(32,201,151,.32), transparent 68%)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 1 }}>
          <Box component="img" src="/omnitrace_logo.png" alt="OmniTrace" sx={{ width: 44, height: 44, filter: 'drop-shadow(0 8px 18px rgba(0,0,0,.2))' }} />
          <Typography variant="h5" fontWeight={800}>OmniTrace</Typography>
        </Box>

        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 650 }}>
          <Chip
            icon={<Radar sx={{ color: '#7CE8C8 !important' }} />}
            label={t('auth.liveIntelligence')}
            sx={{ mb: 3, color: '#DDF9F1', bgcolor: 'rgba(32,201,151,.13)', border: '1px solid rgba(124,232,200,.24)' }}
          />
          <Typography variant="h2" sx={{ fontSize: { lg: 48, xl: 60 }, lineHeight: 1.03, mb: 2.5 }}>
            {t('auth.heroLine1')}<br />{t('auth.heroLine2')}
          </Typography>
          <Typography sx={{ maxWidth: 560, color: 'rgba(235,246,255,.72)', fontSize: 18, lineHeight: 1.7 }}>
            {t('auth.heroSubtitle')}
          </Typography>
          <Stack direction="row" spacing={3} sx={{ mt: 5 }}>
            {[
              [<AutoGraph key="a" />, t('auth.realTimeInsight')],
              [<VerifiedUser key="b" />, t('auth.secureDesign')],
            ].map(([icon, label]) => (
              <Box key={String(label)} sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,.88)' }}>
                {icon}<Typography variant="body2" fontWeight={700}>{label}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Typography variant="caption" sx={{ position: 'relative', zIndex: 1, color: 'rgba(255,255,255,.45)' }}>
          {t('auth.brandTagline')}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minWidth: 0, minHeight: { xs: '100vh', lg: 'auto' }, p: { xs: 2, sm: 5 } }}>
        <Outlet />
      </Box>
    </Box>
  )
}
